<script setup lang="ts">
import { computed, defineAsyncComponent, onErrorCaptured, ref } from 'vue'
import ErrorBoundaryPage from '@/pages/public/ErrorBoundaryPage.vue'
import NotificationStack from '@/components/common/NotificationStack.vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Lazy, and gated, for two separate reasons.
 *
 * LAZY: a static import here put `composables/useBookings` — twelve
 * `httpsCallable` bindings — into the EAGER entry chunk, which every public
 * visitor downloads before the landing page paints. Measured in the build
 * output: `resendBookingConfirmation` appeared in `index-*.js` rather than a
 * route chunk. (No new Firebase SDK was pulled in: `router/index.ts` →
 * `useRoleGuard` → `stores/auth` → `lib/firebase` already made that eager.
 * The module itself was the regression.) An async component keeps it in its
 * own chunk, fetched only when the badge is actually rendered.
 *
 * GATED: `v-if` on the role rather than inside the component, so the chunk is
 * never even requested for a visitor who could not see it. This is what makes
 * "lazy" worth anything — an async component rendered unconditionally still
 * downloads on every page.
 *
 * `admin-viewer` is deliberately excluded, unlike most staff surfaces it can
 * read. The badge is a call to ACT: verify a transfer, press Mark Paid. A
 * view-only account cannot do either — every write path rejects it — so all
 * this would do is show them a number that never goes down and that they have
 * no way to clear.
 */
const PendingBookingsBadge = defineAsyncComponent(
  () => import('@/components/staff/PendingBookingsBadge.vue'),
)

const auth = useAuthStore()
const canActOnBookings = computed(() => auth.role === 'staff' || auth.role === 'admin')

const error = ref(false)

onErrorCaptured((err) => {
  // Log the real error for debugging — never show it to the user, it's
  // often a raw/minified technical message (e.g. a production TDZ error
  // like "Cannot access 'Q' before initialization") that means nothing to
  // them and can't be acted on anyway.
  console.error(err)
  error.value = true
  return false
})
</script>

<template>
  <ErrorBoundaryPage v-if="error" />
  <RouterView v-else />
  <NotificationStack />
  <!-- Kept outside the ErrorBoundaryPage branch on purpose: if a page has
       crashed, an unverified payment is still waiting and staff should still
       be told. The component itself also stays silent when nothing is
       pending, and on the lobby TV routes. -->
  <PendingBookingsBadge v-if="canActOnBookings" />
</template>

<style scoped></style>
